import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";

import { novaClient } from "@/lib/trpc";
import { SkillType } from "@/types";

// All skill CRUD in one hook. Writes change the profile served by the
// sheet-data query, so each mutation updates that cache in place at the hook
// level — every sheet view stays in sync even when the calling editor
// unmounts before the mutation resolves.
export function useSkill() {
  const queryClient = useQueryClient();

  const setSheetDataSkills = (
    userId: number,
    updater: (skills: SkillType[]) => SkillType[],
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const [, params] = query.queryKey as [
            unknown,
            { input?: { userId?: number } }?,
          ];
          return params?.input?.userId === userId;
        },
        queryKey: getQueryKey(
          novaClient.internal.user.active.getCandidacySheetData,
        ),
      },
      (cached: unknown) => {
        if (!cached) return cached;
        const data = cached as { skills?: SkillType[] };
        return { ...data, skills: updater(data.skills ?? []) };
      },
    );
  };

  const createSkill = novaClient.internal.user.profile.skill.create.useMutation(
    {
      onSuccess: (created, variables) => {
        setSheetDataSkills(variables.userId, (skills) => [
          ...skills,
          created as SkillType,
        ]);
      },
    },
  );

  const deleteSkill = novaClient.internal.user.profile.skill.delete.useMutation(
    {
      onSuccess: (_deleted, variables) => {
        setSheetDataSkills(variables.userId, (skills) =>
          skills.filter((skill) => skill.id !== variables.id),
        );
      },
    },
  );

  const bulkDeleteSkills =
    novaClient.internal.user.profile.skill.bulkDelete.useMutation({
      onSuccess: (_deleted, variables) => {
        setSheetDataSkills(variables.userId, () => []);
      },
    });

  return { bulkDeleteSkills, createSkill, deleteSkill };
}
